import type { MembershipDto } from "@bafe/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";

import { api } from "../lib/api.js";
import { presentError } from "../lib/errors.js";
import { canOpenPlatformWorkspace } from "../lib/platform-navigation.js";
import { USER_DASHBOARD_HOME } from "../lib/user-dashboard.js";
import { useSession } from "../lib/session.js";
import { isCustomerWebmailPath } from "../lib/webmail.js";
import { membershipForSlug, parseWorkspaceLocation, workspaceHref } from "../lib/workspace-url.js";
import { useOrganisationStore } from "../lib/stores.js";

export function WorkspaceScope() {
	const { workspaceSlug = "" } = useParams();
	const location = useLocation();
	const session = useSession();
	const queryClient = useQueryClient();
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);
	const [error, setError] = useState<string | null>(null);
	const [ready, setReady] = useState(false);

	useEffect(() => {
		setReady(false);
		setError(null);
		if (!session.data || !workspaceSlug) return;

		let cancelled = false;

		async function sync() {
			const memberships = session.data?.memberships ?? [];
			let membership: MembershipDto | null = membershipForSlug(memberships, workspaceSlug);
			if (!membership) {
				try {
					const resolved = await api.resolveWorkspaceSlug(workspaceSlug);
					membership =
						memberships.find((item) => item.organisationId === resolved.organisationId) ?? null;
					if (membership && membership.organisationSlug !== workspaceSlug) {
						const target = location.pathname.replace(
							`/workspace/${workspaceSlug}`,
							`/workspace/${membership.organisationSlug}`,
						);
						if (!cancelled) window.location.replace(`${target}${location.search}`);
						return;
					}
				} catch {
					membership = null;
				}
			}

			if (!membership) {
				if (!cancelled) setError("This workspace is not in your account.");
				return;
			}

			if (membership.organisationSlug !== workspaceSlug) {
				const target = location.pathname.replace(
					`/workspace/${workspaceSlug}`,
					`/workspace/${membership.organisationSlug}`,
				);
				if (!cancelled) window.location.replace(`${target}${location.search}`);
				return;
			}

			if (session.data?.activeOrganisationId !== membership.organisationId) {
				try {
					const result = await api.selectWorkspace(membership.organisationId);
					setOrganisationId(result.organisationId);
					await queryClient.invalidateQueries();
				} catch (caught) {
					if (!cancelled) setError(presentError(caught).title);
					return;
				}
			}

			if (!cancelled) setReady(true);
		}

		void sync();
		return () => {
			cancelled = true;
		};
	}, [
		location.pathname,
		location.search,
		queryClient,
		session.data,
		setOrganisationId,
		workspaceSlug,
	]);

	if (error) {
		return (
			<div className="card">
				<h2>Workspace unavailable</h2>
				<p className="sub">{error}</p>
			</div>
		);
	}

	if (!ready) return <p className="empty">Loading workspace.</p>;
	return <Outlet />;
}

const FLAT_LEGACY: Record<string, string> = {
	"/settings/organisation": "manage/workspace-settings",
	"/manage/settings": "manage/workspace-settings",
	"/domains": "manage/domains",
	"/mailboxes": "manage/email/mailboxes",
	"/aliases": "manage/email/aliases",
	"/forwarders": "manage/email/forwarders",
	"/migration": "manage/email/migration",
	"/billing": "manage/billing",
	"/settings/team": "manage/access",
	"/phone/numbers": "manage/phone/numbers",
	"/hub/activity": "manage/activity",
	"/hub/integration": "manage/integration",
};

export function workspaceLegacySuffix(path: string): string {
	if (path === "/" || path === "/manage") return "manage";
	const flat = FLAT_LEGACY[path];
	if (flat) return flat;
	if (
		path.startsWith("/manage/") ||
		path.startsWith("/hub/") ||
		path.startsWith("/mail/") ||
		path.startsWith("/site/") ||
		path.startsWith("/team/") ||
		path.startsWith("/social/") ||
		path.startsWith("/client/") ||
		path.startsWith("/phone/") ||
		path.startsWith("/engage/")
	) {
		return path.slice(1);
	}
	if (path.startsWith("/domains/")) return `manage/domains${path.slice("/domains".length)}`;
	if (path.startsWith("/mailboxes/")) return `manage/email/mailboxes${path.slice("/mailboxes".length)}`;
	if (path === "/hub") return "";
	return "hub";
}

function CustomerWebmailHardRedirect() {
	const location = useLocation();
	useEffect(() => {
		window.location.replace(`${window.location.origin}${location.pathname}${location.search}`);
	}, [location.pathname, location.search]);
	return <p className="empty">Opening mail…</p>;
}

export function WorkspaceLegacyRedirect() {
	const session = useSession();
	const location = useLocation();
	const slug =
		session.data?.memberships.find(
			(item) => item.organisationId === session.data?.activeOrganisationId,
		)?.organisationSlug ??
		session.data?.memberships[0]?.organisationSlug ??
		null;

	const path = location.pathname.split("?")[0] ?? location.pathname;
	if (isCustomerWebmailPath(path)) {
		return <CustomerWebmailHardRedirect />;
	}
	if (path === "/platform" || path.startsWith("/platform/")) {
		const target = path === "/platform" ? "/platform/workspace" : path;
		return <Navigate to={`${target}${location.search}`} replace state={location.state} />;
	}

	if (!slug) return <Navigate to={USER_DASHBOARD_HOME} replace />;

	return (
		<Navigate
			to={`${workspaceHref(slug, workspaceLegacySuffix(path))}${location.search}`}
			replace
			state={location.state}
		/>
	);
}

export function WorkspaceRootRedirect() {
	return <Navigate to={USER_DASHBOARD_HOME} replace />;
}

function activeWorkspaceSlug(session: ReturnType<typeof useSession>): string | null {
	return (
		session.data?.memberships.find(
			(item) => item.organisationId === session.data?.activeOrganisationId,
		)?.organisationSlug ??
		session.data?.memberships[0]?.organisationSlug ??
		null
	);
}

/** Legacy slug-nested platform URLs → global `/platform/*`. */
export function WorkspaceNestedPlatformRedirect() {
	const { workspaceSlug = "" } = useParams();
	const location = useLocation();
	const path = location.pathname.split("?")[0] ?? location.pathname;
	const prefix = `/workspace/${workspaceSlug}/platform`;
	if (!path.startsWith(prefix)) {
		return <Navigate to="/platform/workspace" replace />;
	}
	const rest = path.slice(prefix.length);
	const target = rest ? `/platform${rest}` : "/platform/workspace";
	return <Navigate to={`${target}${location.search}`} replace state={location.state} />;
}

/** Redirect `/platform/users` style paths into the active workspace manage tree. */
export function PlatformToManageRedirect({ managePath }: { managePath: string }) {
	const session = useSession();
	const slug = activeWorkspaceSlug(session);
	if (!slug) return <Navigate to={USER_DASHBOARD_HOME} replace />;
	return <Navigate to={workspaceHref(slug, managePath)} replace />;
}

export function PlatformWorkspaceGate() {
	const session = useSession();
	const allowed = canOpenPlatformWorkspace({
		platformOwner: session.data?.platformOwner ?? false,
	});
	if (!allowed) {
		return <Navigate to={USER_DASHBOARD_HOME} replace />;
	}
	return <Outlet />;
}

export function isWorkspaceScopedPath(pathname: string): boolean {
	return parseWorkspaceLocation(pathname) !== null;
}
